// Sendefenster des Workflows (Runde 10): Wochentage, Uhrzeiten, Zeitzone, Streuung, Blackout Dates beachten.
// Das Fenster bestimmt nur, WANN ein E-Mail- oder SMS-Schritt (auf Wunsch auch eine Aufgabe) an die Reihe kommt. Mails und SMS
// bleiben Entwürfe zur Prüfung, hier wird nichts gesendet. Darunter eine Vorschau der nächsten Zeitpunkte vom Server.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/Icon";
import { zeitpunktLang } from "../werkzeuge/zeit";
import { FENSTER_STANDARD, WOCHENTAGE_KURZ, WOCHENTAGE_LANG, fensterText } from "./teil2Hilfen";
import { workflows2Api, type Sendefenster, type VorschauAntwort } from "./teil2Api";

interface Props {
  fenster: Sendefenster;
  blackoutBeachten: boolean;
  onFenster: (f: Sendefenster) => void;
  onBlackout: (an: boolean) => void;
}

function Haken({ an, onChange, children }: { an: boolean; onChange: (an: boolean) => void; children: React.ReactNode }) {
  return (
    <label className="wf-haken">
      <input type="checkbox" checked={an} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

export function SendefensterEditor({ fenster, blackoutBeachten, onFenster, onBlackout }: Props) {
  const [vorschau, setVorschau] = useState<VorschauAntwort | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const set = (teil: Partial<Sendefenster>) => onFenster({ ...fenster, ...teil });
  const tageWechseln = (t: number) => {
    const neu = fenster.tage.includes(t) ? fenster.tage.filter((x) => x !== t) : [...fenster.tage, t].sort((a, b) => a - b);
    set({ tage: neu });
  };
  const ungueltig = fenster.aktiv && (fenster.tage.length === 0 || fenster.von >= fenster.bis);

  // Vorschau der nächsten Zeitpunkte, kurz nach der letzten Änderung
  const schluessel = JSON.stringify([fenster, blackoutBeachten]);
  useEffect(() => {
    if (ungueltig) {
      setVorschau(null);
      setFehler(fenster.tage.length === 0 ? "Wähle mindestens einen Wochentag." : "„Von“ muss vor „bis“ liegen.");
      return;
    }
    let weg = false;
    const id = window.setTimeout(() => {
      workflows2Api
        .vorschau(fenster, blackoutBeachten, 5)
        .then((r) => {
          if (!weg) {
            setVorschau(r);
            setFehler(null);
          }
        })
        .catch(() => {
          if (!weg) setFehler("Die Vorschau ließ sich nicht laden.");
        });
    }, 350);
    return () => {
      weg = true;
      window.clearTimeout(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schluessel]);

  return (
    <div className="wf-form wf-fenster">
      <Haken an={fenster.aktiv} onChange={(an) => set({ aktiv: an, ...(an && !fenster.tage.length ? { tage: FENSTER_STANDARD.tage } : {}) })}>
        <strong>Sendefenster verwenden</strong> <span className="leise">· E-Mail- und SMS-Schritte laufen nur zu diesen Zeiten</span>
      </Haken>

      {fenster.aktiv ? (
        <>
          <div className="wf-form__zeile">
            <div className="feld wf-feld">
              <span className="feld__label">Wochentage</span>
              <div className="wf-tage" role="group" aria-label="Wochentage">
                {WOCHENTAGE_KURZ.map((kurz, t) => (
                  <button
                    key={kurz}
                    type="button"
                    className={`filterchip${fenster.tage.includes(t) ? " filterchip--an" : ""}`}
                    aria-pressed={fenster.tage.includes(t)}
                    title={WOCHENTAGE_LANG[t]}
                    onClick={() => tageWechseln(t)}
                  >
                    {kurz}
                  </button>
                ))}
              </div>
            </div>
            <div className="feld wf-feld">
              <span className="feld__label">Uhrzeit</span>
              <span className="wf-inline">
                <input className="eingabe eingabe--uhrzeit" type="time" value={fenster.von} onChange={(e) => set({ von: e.target.value || "09:00" })} aria-label="Sendefenster von" />
                <span className="leise">bis</span>
                <input className="eingabe eingabe--uhrzeit" type="time" value={fenster.bis === "24:00" ? "23:59" : fenster.bis} onChange={(e) => set({ bis: e.target.value || "16:00" })} aria-label="Sendefenster bis" />
                <span className="leise">Uhr</span>
              </span>
            </div>
          </div>

          <div className="wf-form__zeile">
            <div className="feld wf-feld">
              <span className="feld__label">Zeitzone</span>
              <div className="segment" role="group" aria-label="Zeitzone des Fensters">
                {(["organisation", "kontakt"] as const).map((z) => (
                  <button key={z} type="button" className={`segment__knopf${fenster.zeitzone === z ? " segment__knopf--an" : ""}`} onClick={() => set({ zeitzone: z })}>
                    {z === "organisation" ? "Organisation" : "Ortszeit des Kontakts"}
                  </button>
                ))}
              </div>
              <span className="leise wf-feld__hinweis">
                {fenster.zeitzone === "organisation"
                  ? "Die Zeiten gelten in der Zeitzone der Organisation (Einstellungen › Organisation)."
                  : "Die Zeiten gelten dort, wo der Kontakt wohnt, erkannt an der Vorwahl seiner Nummer. Ohne Nummer gilt die Zeitzone der Organisation."}
              </span>
            </div>
            <div className="feld wf-feld">
              <span className="feld__label">Streuung</span>
              <span className="wf-inline">
                <input className="eingabe eingabe--schmal" type="number" min={0} max={120} value={fenster.streuung_min} onChange={(e) => set({ streuung_min: Math.max(0, Math.min(120, Number(e.target.value) || 0)) })} aria-label="Streuung in Minuten" />
                <span className="leise">Minuten</span>
              </span>
              <span className="leise wf-feld__hinweis">Wird ein Schritt auf den Beginn des Fensters geschoben, kommt eine zufällige Zeit bis zu diesem Wert dazu. So werden nicht alle Leads zur selben Minute fällig.</span>
            </div>
          </div>

          <Haken an={fenster.auch_aufgaben} onChange={(an) => set({ auch_aufgaben: an })}>
            Auch Aufgaben und Anrufaufgaben erst im Fenster anlegen
          </Haken>
        </>
      ) : (
        <p className="leise wf-form__text">Ohne Sendefenster kommt jeder Schritt sofort dran, sobald seine Wartezeit um ist.</p>
      )}

      <Haken an={blackoutBeachten} onChange={onBlackout}>
        Blackout Dates beachten{" "}
        <Link to="/workflows/blackout" className="wf-link">
          Blackout Dates verwalten
        </Link>
      </Haken>

      <div className="wf-vorschau" aria-live="polite">
        <Icon name="uhr" groesse={14} />
        {fehler ? (
          <span className="text-gefahr">{fehler}</span>
        ) : vorschau ? (
          <span>
            <strong>{fenster.aktiv ? fensterText(fenster) : "Immer"}</strong>
            {vorschau.naechste.length ? (
              <>
                <span className="leise"> · nächste Zeitpunkte ab jetzt: </span>
                {vorschau.naechste.map((z, i) => (
                  <span key={z} className="wf-vorschau__zeit">
                    {zeitpunktLang(z).replace(/ Uhr$/, "")}
                    {i < vorschau.naechste.length - 1 ? " · " : ""}
                  </span>
                ))}
              </>
            ) : (
              <span className="leise"> · kein erlaubter Zeitpunkt in Sicht</span>
            )}
          </span>
        ) : (
          <span className="leise">Vorschau wird geladen …</span>
        )}
      </div>
      <p className="leise wf-form__text">
        Das Fenster legt nur fest, wann ein Entwurf fällig wird. Gesendet wird nichts von allein: Mails und SMS bleiben Entwürfe, die du prüfst und abschickst.
      </p>
    </div>
  );
}
